import TrunkType from '@/utils/enums/TrunkType';
import Statuses from '../../utils/statuses';
import {
    DR_TRUNK_TYPE_NAME,
    OC_MOBILE_USAGE,
    OC_TRUNK_TYPE_NAME,
    TRUNK_KINDS,
} from '../constants';

export const digitsOnly = (value) => String(value ?? '').replace(/\D/g, '');

/**
 * Classifies a Teams trunk as Operator Connect, Direct Routing or other.
 * Direct Routing has no stable trunk type ID (`TrunkType.DIRECT_ROUTING` is
 * empty), so it is matched by trunk type name like the Trunks page does.
 */
export const getTrunkKind = (trunk) => {
    if (!trunk) return TRUNK_KINDS.NONE;
    if (
        trunk.trunkTypeID === TrunkType.OPERATOR_CONNECT ||
        trunk.trunkType?.name === OC_TRUNK_TYPE_NAME
    ) {
        return TRUNK_KINDS.OC;
    }
    if (trunk.trunkType?.name === DR_TRUNK_TYPE_NAME) return TRUNK_KINDS.DR;
    return TRUNK_KINDS.OTHER;
};

/** Which Teams trunk types the company has, taken from its trunks. */
export const getTenantTrunkKinds = (trunks = []) => ({
    oc: trunks.some((trunk) => getTrunkKind(trunk) === TRUNK_KINDS.OC),
    dr: trunks.some((trunk) => getTrunkKind(trunk) === TRUNK_KINDS.DR),
});

// Disabled is derived, not stored: an OK number with no trunk.
export const getStatusKey = (row) =>
    row.platformStatus === 0 && row.trunkID == null
        ? 'disabled'
        : row.platformStatus;

/**
 * Why a number can't be ticked for a bulk update, or '' when it can. Numbers
 * still processing, or with no Teams user, are left alone; Teams Phone Mobile
 * numbers have their own disassociate flow.
 */
export const getUnselectableReason = ({ statusLabel, userId, isMobile }) => {
    if (isMobile) return 'Teams Phone Mobile (managed separately)';
    if (statusLabel === 'Processing') return 'Processing';
    if (!userId) return 'No user assigned';
    return '';
};

const toBulkRow = (row, lookups) => {
    const { trunksById, profilesById, usagesById, countriesById } = lookups;
    const hasTrunk = row.trunkID != null;
    const trunk = trunksById.get(row.trunkID);
    const country =
        countriesById.get(row.countryID) ??
        countriesById.get(row.countryCodeID);
    const digits = digitsOnly(row.telephoneNumber);
    const overstampDigits = digitsOnly(row.overstampNumber);
    const statusKey = getStatusKey(row);
    const statusLabel = Statuses[statusKey]?.displayedName ?? 'Unknown';
    const isMobile = row.operatorConnectUsageID === OC_MOBILE_USAGE;

    let trunkKind = TRUNK_KINDS.NONE;
    if (hasTrunk) trunkKind = trunk ? getTrunkKind(trunk) : TRUNK_KINDS.OTHER;

    return {
        id: row.numberID,
        // Read by NumberCellRenderer to find the flag
        countryID: row.countryID,
        countryCodeID: row.countryCodeID,
        telephoneNumber: row.telephoneNumber,
        digits,
        e164: digits ? `+${digits}` : '',
        countryCode: country?.alpha2 ?? '',
        countryName: country?.name ?? '',
        statusKey,
        statusLabel,
        statusMessage: row.statusMessage,
        operatorStatus: row.operatorStatus,
        operatorMessage: row.operatorMessage,
        trunkID: hasTrunk ? row.trunkID : null,
        trunkKind,
        trunkName: hasTrunk ? (trunk?.name ?? row.trunkName ?? '') : '',
        callingProfileID: row.operatorConnectCallingProfileID ?? '',
        callingProfileName:
            profilesById.get(row.operatorConnectCallingProfileID)?.name ??
            row.operatorConnectCallingProfile ??
            '',
        usageID: row.operatorConnectUsageID ?? '',
        usageName:
            usagesById.get(row.operatorConnectUsageID)?.displayName ??
            row.operatorConnectUsageID ??
            '',
        overstamp: overstampDigits ? `+${overstampDigits}` : '',
        isMobile,
        // The Teams user the number is assigned to (null when unassigned)
        userId: row.userID ?? null,
        userName: row.user ?? '',
        unselectableReason: getUnselectableReason({
            statusLabel,
            userId: row.userID,
            isMobile,
        }),
        raw: row,
    };
};

/**
 * Turns the Teams platform numbers response into flat rows for the bulk
 * operations lists, sorted by number.
 *
 * @param {object[]} platformNumbers rows from `/Company/{c}/PlatformNumbers/{teams}`
 * @param {{ trunks?: object[], usages?: object[], countries?: object[] }} sources
 * @returns {object[]}
 */
export const toBulkRows = (
    platformNumbers = [],
    { trunks = [], usages = [], countries = [] } = {},
) => {
    const lookups = {
        trunksById: new Map(trunks.map((trunk) => [trunk.id, trunk])),
        profilesById: new Map(
            trunks
                .flatMap((trunk) => trunk.callingProfileList ?? [])
                .map((profile) => [profile.id, profile]),
        ),
        usagesById: new Map(usages.map((usage) => [usage.id, usage])),
        countriesById: new Map(
            countries.map((country) => [country.id, country]),
        ),
    };

    return platformNumbers
        .map((row) => toBulkRow(row, lookups))
        .sort((a, b) =>
            a.digits.localeCompare(b.digits, undefined, { numeric: true }),
        );
};
